"use client";

import Link from "next/link";

import { createClient } from "@/lib/supabase/client";

export default function LoginPage() {
    const handleGoogleLogin = async () => {
        const supabase = createClient();

        await supabase.auth.signInWithOAuth({
            provider: "google",
            options: {
                redirectTo: `${process.env.NEXT_PUBLIC_APP_URL}/auth/callback`,
            },
        });
    };

    return (
        <div className="min-h-screen flex w-full bg-[var(--bg-app)]">
            {/* Left Section: Login Form */}
            <div className="flex-1 flex flex-col justify-center px-4 py-12 sm:px-6 lg:flex-none lg:px-20 xl:px-24 w-full lg:w-[50%] xl:w-[45%] border-r border-[var(--border-subtle)]">
                <div className="mx-auto w-full max-w-sm lg:w-96">
                    {/* Logo */}
                    <div className="flex items-center gap-3 mb-10">
                        <img src="/logo.png" alt="NectarAI Logo" className="h-10 w-10 object-contain" />
                        <span className="font-serif text-xl font-semibold text-[var(--text-primary)]">
                            NectarAI
                        </span>
                    </div>

                    <div>
                        <h2 className="font-serif mt-6 text-3xl font-semibold text-[var(--text-primary)] leading-tight animate-ink-bleed">
                            Welcome back
                        </h2>
                        <p className="mt-2 text-[var(--text-secondary)]">
                            Sign in to continue to your dashboard
                        </p>
                    </div>

                    <div className="mt-10">
                        <div className="flex flex-col gap-4">
                            <button
                                type="button"
                                onClick={handleGoogleLogin}
                                className="group flex w-full items-center justify-center gap-3 rounded-lg bg-[var(--bg-card)] px-4 py-3 text-sm font-medium text-[var(--text-primary)] border border-[var(--border-default)] hover:border-[var(--border-strong)] hover:bg-[var(--bg-hover)] transition-all cursor-pointer"
                            >
                                <svg aria-hidden="true" className="h-5 w-5" viewBox="0 0 24 24">
                                    <path d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z" fill="#4285F4" />
                                    <path d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" fill="#34A853" />
                                    <path d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z" fill="#FBBC05" />
                                    <path d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" fill="#EA4335" />
                                </svg>
                                <span>Continue with Google</span>
                            </button>
                        </div>

                        <div className="mt-8 text-center">
                            <p className="text-xs text-[var(--text-muted)]">
                                By signing in, you agree to our{" "}
                                <Link href="/terms-of-service" className="underline underline-offset-2 hover:text-emerald-400 transition-colors">Terms of Service</Link>
                                {" "}and{" "}
                                <Link href="/privacy-policy" className="underline underline-offset-2 hover:text-emerald-400 transition-colors">Privacy Policy</Link>
                            </p>
                        </div>
                    </div>
                </div>
            </div>

            {/* Right Section: Hero */}
            <div className="hidden lg:flex lg:flex-1 relative bg-[var(--bg-app)] overflow-hidden">
                {/* Decorative gradient */}
                <div className="absolute top-1/4 left-1/2 w-[600px] h-[600px] -translate-x-1/2 -translate-y-1/4">
                    <div className="absolute inset-0 bg-gradient-radial from-emerald-500/20 via-emerald-500/5 to-transparent rounded-full blur-3xl"></div>
                </div>

                {/* Grid pattern */}
                <div className="absolute inset-0 opacity-20" style={{
                    backgroundImage: `radial-gradient(circle at 1px 1px, rgba(16,185,129,0.3) 1px, transparent 0)`,
                    backgroundSize: '48px 48px'
                }}></div>

                {/* Content */}
                <div className="relative z-10 flex flex-col justify-end w-full p-16 xl:p-24">
                    <div className="max-w-xl">
                        <div className="inline-flex items-center rounded-lg bg-emerald-500/10 border border-emerald-500/20 px-4 py-2 text-sm font-medium text-emerald-400 mb-6">
                            <span className="material-symbols-outlined text-lg mr-2 animate-status-pulse">auto_awesome</span>
                            AI-Powered Strategy
                        </div>
                        <h1 className="font-serif text-4xl font-semibold text-[var(--text-primary)] sm:text-5xl mb-6 leading-tight animate-ink-bleed">
                            Automate your brand with AI precision.
                        </h1>
                        <p className="mt-4 text-lg text-[var(--text-secondary)] leading-relaxed max-w-lg">
                            NectarAI uses your Business DNA to create and publish content
                            automatically, saving you hours every week.
                        </p>

                        {/* Feature list */}
                        <div className="mt-8 flex flex-col gap-3">
                            <div className="flex items-center gap-3 text-[var(--text-secondary)]">
                                <span className="material-symbols-outlined text-emerald-400">check_circle</span>
                                <span>AI-generated campaigns tailored to your brand</span>
                            </div>
                            <div className="flex items-center gap-3 text-[var(--text-secondary)]">
                                <span className="material-symbols-outlined text-emerald-400">check_circle</span>
                                <span>Automated posting across all platforms</span>
                            </div>
                            <div className="flex items-center gap-3 text-[var(--text-secondary)]">
                                <span className="material-symbols-outlined text-emerald-400">check_circle</span>
                                <span>Content calendar with drag-and-drop scheduling</span>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
}
